import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Users, ArrowRight } from "lucide-react";
import { TEAMS, SCHEDULE } from "../data/tournament";
import { api } from "../lib/api";

const Equipes = () => {
  const [filter, setFilter] = useState("Toutes");
  const [serverTeams, setServerTeams] = useState({});

  useEffect(() => {
    api.get("/teams").then((r) => {
      const m = {};
      r.data.forEach((t) => (m[t.team_id] = t));
      setServerTeams(m);
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (filter === "Toutes") return TEAMS;
    return TEAMS.filter((t) => t.category === filter);
  }, [filter]);

  const gamesByTeam = useMemo(() => {
    const map = {};
    SCHEDULE.forEach((g) => {
      [g.away, g.home].forEach((t) => {
        if (!map[t]) map[t] = 0;
        map[t]++;
      });
    });
    return map;
  }, []);

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen" data-testid="equipes-page">
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
            La relève sur glace
          </span>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl lg:text-8xl uppercase leading-none">
            Les <span className="text-[#E60012]">équipes</span>
          </h1>
          <p className="mt-4 text-white/60 max-w-2xl">
            Cliquez sur une équipe pour voir son alignement.
          </p>

          <div className="mt-8 flex items-center gap-2 flex-wrap">
            {["Toutes", "2017", "2018"].map((c) => (
              <button
                key={c}
                data-testid={`team-filter-${c.toLowerCase()}`}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 font-condensed uppercase tracking-[0.2em] text-xs transition-colors ${
                  filter === c
                    ? "bg-[#E60012] text-white"
                    : "border border-white/15 text-white/70 hover:border-[#E60012] hover:text-white"
                }`}
              >
                {c === "Toutes" ? "Toutes" : `Catégorie ${c}`}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((team, i) => {
            const server = serverTeams[team.id];
            return (
              <motion.div
                key={team.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.5 }}
                className="group relative bg-[#111] border border-white/10 hover:border-[#E60012] overflow-hidden clip-jersey transition-all hover:-translate-y-1"
                data-testid={`team-card-${team.id}`}
              >
                <Link to={`/equipes/${team.id}`} className="block">
                  <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#E60012]" />
                  <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[#E60012]/10 blur-3xl group-hover:bg-[#E60012]/30 transition" />

                  <div className="relative p-7">
                    <div className="flex items-start justify-between mb-6">
                      <div className="w-20 h-20 bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden">
                        {server?.logo_base64 ? (
                          <img src={server.logo_base64} alt={team.name} className="w-full h-full object-contain p-1" />
                        ) : (
                          <Shield className="w-10 h-10 text-[#E60012]/70" strokeWidth={1.5} />
                        )}
                      </div>
                      <span className="font-condensed uppercase tracking-[0.2em] text-xs text-white/60">
                        Cat. {team.category}
                      </span>
                    </div>

                    <h3 className="font-display text-3xl uppercase leading-tight">{team.name}</h3>
                    {server?.motto && (
                      <p className="mt-2 text-white/55 text-xs italic">{server.motto}</p>
                    )}

                    <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between text-xs font-condensed uppercase tracking-[0.2em]">
                      <span className="flex items-center gap-2 text-white/60">
                        <Users className="w-3.5 h-3.5" />
                        M9
                      </span>
                      <span className="text-white/80">
                        {gamesByTeam[team.name] || 0} match{(gamesByTeam[team.name] || 0) > 1 ? "s" : ""}
                      </span>
                      <span className="flex items-center gap-1 text-[#E60012] group-hover:gap-2 transition-all">
                        Détails <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Equipes;
